// \src\pages\sorteio\page.tsx

import { useState, useCallback } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MathCaptcha from "@/components/base/MathCaptcha";
import { useAdmin, type Raffle } from "@/context/AdminContext";

const RAFFLE_API_URL = "/api/sorteio.php";

function getToday(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function isVideoMedia(src: string): boolean {
  return /\.(mp4|webm)(?:[?#].*)?$/i.test(String(src || ""));
}

function formatDate(date: string): string {
  if (!date) {
    return "";
  }

  return new Date(`${date}T12:00:00`).toLocaleDateString("pt-BR");
}

function isStarted(raffle: Raffle): boolean {
  return !raffle.startDate || raffle.startDate <= getToday();
}

function isEnded(raffle: Raffle): boolean {
  return Boolean(
    raffle.endDate &&
      raffle.endDate < getToday(),
  );
}

function isCurrent(raffle: Raffle): boolean {
  return (
    raffle.active &&
    isStarted(raffle) &&
    !isEnded(raffle)
  );
}

function isUpcoming(raffle: Raffle): boolean {
  return Boolean(
    raffle.active &&
      raffle.startDate &&
      raffle.startDate > getToday(),
  );
}

function normalizeCpfInput(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 11);

  return digits
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1-$2");
}

function cpfIsValid(value: string): boolean {
  const cpf = value.replace(/\D/g, "");

  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) {
    return false;
  }

  for (let digit = 9; digit < 11; digit += 1) {
    let sum = 0;

    for (let index = 0; index < digit; index += 1) {
      sum += Number(cpf[index]) * ((digit + 1) - index);
    }

    let check = (10 * sum) % 11;
    if (check === 10) check = 0;
    if (check !== Number(cpf[digit])) return false;
  }

  return true;
}

function nameIsValid(value: string): boolean {
  const name = value.trim();
  return (
    name.length >= 3 &&
    !/\d/u.test(name) &&
    /^[\p{L}\p{M}][\p{L}\p{M}\s.'’\-]+$/u.test(name)
  );
}

function phoneIsValid(value: string): boolean {
  let digits = value.replace(/\D/g, "");

  if (digits.startsWith("55") && (digits.length === 12 || digits.length === 13)) {
    digits = digits.slice(2);
  }

  return (digits.length === 10 || digits.length === 11) && !/^(\d)\1+$/.test(digits);
}

function emailIsValid(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function validateCustomField(
  field: Raffle["fields"][number],
  value: string,
): string | null {
  const trimmed = value.trim();

  if (field.required && !trimmed) {
    return `Preencha o campo: ${field.label}.`;
  }

  if (!trimmed) return null;

  if (field.fieldType === "name" && !nameIsValid(trimmed)) {
    return `Informe um valor válido em ${field.label}, sem números.`;
  }

  if (field.fieldType === "number" && !/^-?\d+(?:[.,]\d+)?$/.test(trimmed)) {
    return `Informe apenas números em ${field.label}.`;
  }

  if (field.fieldType === "email" && !emailIsValid(trimmed)) {
    return `Informe um e-mail válido em ${field.label}.`;
  }

  if (field.fieldType === "phone" && !phoneIsValid(trimmed)) {
    return `Informe um telefone válido em ${field.label}.`;
  }

  if (field.fieldType === "cpf" && !cpfIsValid(trimmed)) {
    return `Informe um CPF válido em ${field.label}.`;
  }

  if (field.fieldType === "date") {
    const parsed = new Date(`${trimmed}T12:00:00`);
    if (Number.isNaN(parsed.getTime())) return `Informe uma data válida em ${field.label}.`;
  }

  if (field.fieldType === "select" && !field.options.includes(trimmed)) {
    return `Selecione uma opção válida em ${field.label}.`;
  }

  return null;
}

export default function Sorteio() {
  const { data } = useAdmin();

  const currentRaffle = data.raffles.find(isCurrent);

  const endedActiveRaffle = [...data.raffles]
    .filter(
      (raffle) =>
        raffle.active &&
        isEnded(raffle),
    )
    .sort((a, b) =>
      (b.endDate || "").localeCompare(
        a.endDate || "",
      ),
    )[0];

  const upcomingRaffle = [...data.raffles]
    .filter(isUpcoming)
    .sort((a, b) =>
      (a.startDate || "").localeCompare(
        b.startDate || "",
      ),
    )[0];

  const featuredRaffle =
    currentRaffle ||
    endedActiveRaffle ||
    upcomingRaffle ||
    null;

  const previousRaffles = [...data.raffles]
    .filter(
      (raffle) =>
        raffle.id !== featuredRaffle?.id &&
        raffle.visibleAfterEnd &&
        isEnded(raffle),
    )
    .sort((a, b) =>
      (b.endDate || "").localeCompare(
        a.endDate || "",
      ),
    );

  const [formStatus, setFormStatus] = useState<
    "idle" | "sending" | "success" | "error"
  >("idle");
  const [formError, setFormError] = useState("");
  const [captchaOk, setCaptchaOk] = useState(false);
  const [
    captchaVerification,
    setCaptchaVerification,
  ] = useState("");
  const [captchaKey, setCaptchaKey] = useState(0);
  const [name, setName] = useState("");
  const [cpf, setCpf] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [
    regulationRaffle,
    setRegulationRaffle,
  ] = useState<Raffle | null>(null);

  const handleCaptchaVerify = useCallback(
    (verified: boolean) => {
      setCaptchaOk(verified);
    },
    [],
  );

  const handleCaptchaToken = useCallback(
    (token: string) => {
      setCaptchaVerification(token);
    },
    [],
  );

  const resetCaptcha = () => {
    setCaptchaOk(false);
    setCaptchaVerification("");
    setCaptchaKey((value) => value + 1);
  };

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();

    if (!featuredRaffle || !isCurrent(featuredRaffle)) {
      setFormError(
        "Este sorteio não está disponível para novas inscrições.",
      );
      return;
    }

    if (!captchaOk || !captchaVerification) {
      setFormError(
        "Conclua a verificação de segurança antes de enviar.",
      );
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    const submittedName = String(formData.get("name") || "");
    const submittedCpf = String(formData.get("cpf") || "");
    const submittedEmail = String(formData.get("email") || "");
    const submittedWhatsapp = String(formData.get("whatsapp") || "");

    if (!nameIsValid(submittedName)) {
      setFormError("Informe um nome válido, sem números.");
      return;
    }

    if (!cpfIsValid(submittedCpf)) {
      setFormError("Informe um CPF válido.");
      return;
    }

    if (!emailIsValid(submittedEmail)) {
      setFormError("Informe um e-mail válido.");
      return;
    }

    if (!phoneIsValid(submittedWhatsapp)) {
      setFormError("Informe um WhatsApp válido com DDD.");
      return;
    }

    if (featuredRaffle.question && !String(formData.get("answer") || "").trim()) {
      setFormError("Responda à pergunta do sorteio.");
      return;
    }

    for (const field of featuredRaffle.fields.filter(item => item.active)) {
      const value = String(formData.get(`field_${field.id}`) || "");
      const validationError = validateCustomField(field, value);

      if (validationError) {
        setFormError(validationError);
        return;
      }
    }

    setFormStatus("sending");
    setFormError("");

    const payload = new URLSearchParams();

    formData.forEach((value, key) => {
      if (typeof value === "string") {
        payload.append(key, value);
      }
    });

    payload.set(
      "raffle_id",
      String(featuredRaffle.id),
    );
    payload.set(
      "captcha_verification",
      captchaVerification,
    );

    try {
      const response = await fetch(
        RAFFLE_API_URL,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type":
              "application/x-www-form-urlencoded",
            Accept: "application/json",
          },
          body: payload.toString(),
        },
      );

      const responseText = await response.text();

      let result: {
        success?: boolean;
        message?: string;
        code?: string;
      } = {};

      try {
        result = JSON.parse(responseText);
      } catch {
        /* resposta não JSON */
      }

      if (
        response.ok &&
        (result.success === true ||
          result.code === "OK")
      ) {
        setFormStatus("success");
        form.reset();
        setName("");
        setCpf("");
        setWhatsapp("");
        resetCaptcha();
        return;
      }

      setFormStatus("error");
      setFormError(
        result.message ||
          responseText ||
          "Não foi possível concluir sua inscrição.",
      );

      resetCaptcha();
    } catch {
      setFormStatus("error");
      setFormError(
        "Falha de conexão. Verifique sua internet e tente novamente.",
      );
      resetCaptcha();
    }
  };

  const raffleEnded =
    featuredRaffle
      ? isEnded(featuredRaffle)
      : false;

  const raffleUpcoming =
    featuredRaffle
      ? isUpcoming(featuredRaffle)
      : false;

  return (
    <>
      <Navbar />

      <main className="pt-20 min-h-screen bg-background-50">
        <section className="bg-background-200 border-b border-background-300 py-8 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
              Sorteio M&amp;Z
            </h1>

            <p className="text-foreground-500 text-sm mt-2">
              Participe dos nossos sorteios e acompanhe os ganhadores.
            </p>
          </div>
        </section>

        {featuredRaffle ? (
          <>
            <section className="max-w-7xl mx-auto px-4 md:px-8 pt-10">
              <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-50 shadow-sm">
                <div className="relative min-h-[240px] md:min-h-[380px] bg-background-200">
                  {featuredRaffle.bannerImage ? (
                    isVideoMedia(featuredRaffle.bannerImage) ? (
                      <video
                        src={featuredRaffle.bannerImage}
                        className="absolute inset-0 w-full h-full object-cover"
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        aria-label={featuredRaffle.title}
                      />
                    ) : (
                      <img
                        src={featuredRaffle.bannerImage}
                        alt={featuredRaffle.title}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    )
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-primary-100 text-primary-500">
                      <i className="ri-gift-2-line text-8xl" />
                    </div>
                  )}

                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                  <div className="absolute left-0 right-0 bottom-0 p-6 md:p-10 text-background-50">
                    <span className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-xs font-label font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-3">
                      {raffleEnded
                        ? "Sorteio encerrado"
                        : raffleUpcoming
                          ? "Em breve"
                          : "Sorteio vigente"}
                    </span>

                    <h2 className="text-3xl md:text-5xl font-heading font-bold leading-tight">
                      {featuredRaffle.title}
                    </h2>

                    {featuredRaffle.description && (
                      <p className="text-background-200 text-sm md:text-base max-w-2xl mt-3 leading-relaxed">
                        {featuredRaffle.description}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-4 mt-4 text-xs md:text-sm font-label text-background-200">
                      {featuredRaffle.startDate && (
                        <span>
                          <i className="ri-calendar-check-line mr-1" />
                          Início: {formatDate(featuredRaffle.startDate)}
                        </span>
                      )}

                      {featuredRaffle.endDate && (
                        <span>
                          <i className="ri-calendar-close-line mr-1" />
                          Encerramento: {formatDate(featuredRaffle.endDate)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {featuredRaffle.prizes.length > 0 && (
              <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
                <div className="text-center mb-8">
                  <span className="text-primary-600 text-xs font-label font-bold uppercase tracking-widest">
                    O que você pode ganhar
                  </span>

                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-2">
                    Prêmios do Sorteio
                  </h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {featuredRaffle.prizes.map((prize) => (
                    <div
                      key={prize.id}
                      className="bg-background-50 border border-background-200 rounded-xl overflow-hidden"
                    >
                      {prize.image && (
                        <div className="h-52 bg-background-100 overflow-hidden">
                          <img
                            src={prize.image}
                            alt={prize.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}

                      <div className="p-5">
                        <h3 className="font-heading font-bold text-foreground-950 text-lg">
                          {prize.title}
                        </h3>

                        {prize.description && (
                          <p className="text-foreground-600 text-sm leading-relaxed mt-2">
                            {prize.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="max-w-7xl mx-auto px-4 md:px-8 pb-14">
              <div className="flex justify-center mb-8">
                <button
                  type="button"
                  onClick={() =>
                    setRegulationRaffle(featuredRaffle)
                  }
                  className="inline-flex items-center gap-2 bg-foreground-950 hover:bg-primary-500 text-background-50 px-7 py-3 rounded-md font-label font-bold text-sm transition-all cursor-pointer"
                >
                  <i className="ri-file-text-line" />
                  LEIA O REGULAMENTO
                </button>
              </div>

              {raffleEnded ? (
                <div className="bg-primary-50 border border-primary-100 rounded-2xl p-7 md:p-10 text-center">
                  <i className="ri-time-line text-5xl text-primary-500 mb-4 block" />

                  <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-700">
                    SORTEIO ENCERRADO
                  </h2>

                  <p className="text-foreground-600 mt-3">
                    Confira abaixo os ganhadores deste sorteio.
                  </p>
                </div>
              ) : raffleUpcoming ? (
                <div className="bg-background-100 border border-background-200 rounded-2xl p-7 md:p-10 text-center">
                  <i className="ri-calendar-event-line text-5xl text-primary-500 mb-4 block" />

                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                    Sorteio em breve
                  </h2>

                  <p className="text-foreground-600 mt-3">
                    As inscrições serão liberadas em{" "}
                    {formatDate(featuredRaffle.startDate)}.
                  </p>
                </div>
              ) : formStatus === "success" ? (
                <div className="max-w-2xl mx-auto bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
                  <i className="ri-checkbox-circle-fill text-5xl text-green-500 mb-4 block" />

                  <h2 className="text-2xl font-heading font-bold text-green-800">
                    Inscrição realizada!
                  </h2>

                  <p className="text-green-700 text-sm mt-3">
                    Seu cadastro foi recebido com sucesso. Boa sorte!
                  </p>

                  <p className="text-green-700 text-xs mt-2">
                    Cada CPF pode participar apenas uma vez neste sorteio.
                  </p>
                </div>
              ) : (
                <div className="max-w-2xl mx-auto bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8">
                  <div className="text-center mb-7">
                    <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                      Participe do Sorteio
                    </h2>

                    <p className="text-foreground-500 text-sm mt-2">
                      Preencha seus dados abaixo. É permitido apenas um cadastro por CPF neste sorteio.
                    </p>
                  </div>

                  <form
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div
                      className="absolute -left-[9999px] opacity-0 pointer-events-none"
                      aria-hidden="true"
                    >
                      <input
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">
                        Nome Completo *
                      </label>

                      <input
                        type="text"
                        name="name"
                        required
                        value={name}
                        onChange={(event) =>
                          setName(event.target.value.replace(/\d/g, ""))
                        }
                        maxLength={255}
                        autoComplete="name"
                        placeholder="Digite seu nome sem números"
                        className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">
                          CPF *
                        </label>

                        <input
                          type="text"
                          name="cpf"
                          required
                          value={cpf}
                          onChange={(event) =>
                            setCpf(
                              normalizeCpfInput(
                                event.target.value,
                              ),
                            )
                          }
                          maxLength={14}
                          inputMode="numeric"
                          placeholder="000.000.000-00"
                          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">
                          WhatsApp *
                        </label>

                        <input
                          type="tel"
                          name="whatsapp"
                          required
                          value={whatsapp}
                          onChange={(event) =>
                            setWhatsapp(event.target.value.replace(/[^0-9()+\-\s]/g, ""))
                          }
                          maxLength={20}
                          inputMode="tel"
                          autoComplete="tel"
                          placeholder="(11) 99999-9999"
                          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">
                        E-mail *
                      </label>

                      <input
                        type="email"
                        name="email"
                        required
                        maxLength={255}
                        autoComplete="email"
                        placeholder="nome@exemplo.com.br"
                        className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400"
                      />
                    </div>

                    {featuredRaffle.question && (
                      <div>
                        <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">
                          {featuredRaffle.question} *
                        </label>

                        <textarea
                          name="answer"
                          required
                          rows={3}
                          maxLength={1000}
                          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400 resize-y"
                        />
                      </div>
                    )}

                    {featuredRaffle.fields
                      .filter((field) => field.active)
                      .map((field) => (
                        <div key={field.id}>
                          <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">
                            {field.label}{field.required ? " *" : ""}
                          </label>

                          {field.fieldType === "textarea" ? (
                            <textarea
                              name={`field_${field.id}`}
                              required={field.required}
                              rows={3}
                              maxLength={5000}
                              className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400 resize-y"
                            />
                          ) : field.fieldType === "select" ? (
                            <select
                              name={`field_${field.id}`}
                              required={field.required}
                              defaultValue=""
                              className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400"
                            >
                              <option value="">Selecione...</option>
                              {field.options.map((option) => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type={
                                field.fieldType === "email"
                                  ? "email"
                                  : field.fieldType === "number"
                                    ? "text"
                                    : field.fieldType === "date"
                                      ? "date"
                                      : field.fieldType === "phone"
                                        ? "tel"
                                        : "text"
                              }
                              name={`field_${field.id}`}
                              required={field.required}
                              maxLength={field.fieldType === "cpf" ? 14 : 500}
                              inputMode={
                                field.fieldType === "number" || field.fieldType === "cpf"
                                  ? "numeric"
                                  : field.fieldType === "phone"
                                    ? "tel"
                                    : undefined
                              }
                              onInput={
                                field.fieldType === "name"
                                  ? (event) => {
                                      event.currentTarget.value = event.currentTarget.value.replace(/\d/g, "");
                                    }
                                  : field.fieldType === "cpf"
                                    ? (event) => {
                                        event.currentTarget.value = normalizeCpfInput(event.currentTarget.value);
                                      }
                                    : field.fieldType === "phone"
                                      ? (event) => {
                                          event.currentTarget.value = event.currentTarget.value.replace(/[^0-9()+\-\s]/g, "");
                                        }
                                      : undefined
                              }
                              className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md font-label focus:outline-none focus:border-primary-400"
                            />
                          )}
                        </div>
                      ))}

                    <MathCaptcha
                      key={captchaKey}
                      onVerify={handleCaptchaVerify}
                      serverEndpoint={RAFFLE_API_URL}
                      onVerificationToken={handleCaptchaToken}
                    />

                    {formError && (
                      <div className="bg-red-50 border border-red-200 rounded-md p-3">
                        <p className="text-red-700 text-sm font-label flex items-center gap-2">
                          <i className="ri-error-warning-line" />
                          {formError}
                        </p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={
                        formStatus === "sending" ||
                        !captchaOk
                      }
                      className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 py-3 rounded-md font-label font-semibold text-sm transition-all cursor-pointer"
                    >
                      {formStatus === "sending" ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin" />
                          Enviando...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-gift-line" />
                          Confirmar Participação
                        </span>
                      )}
                    </button>
                  </form>
                </div>
              )}
            </section>

            {featuredRaffle.winners.length > 0 && (
              <section className="bg-background-100 border-y border-background-200 py-14 px-4 md:px-8">
                <div className="max-w-7xl mx-auto">
                  <div className="text-center mb-9">
                    <span className="text-primary-600 text-xs font-label font-bold uppercase tracking-widest">
                      Resultado
                    </span>

                    <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-2">
                      Ganhadores
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {featuredRaffle.winners.map((winner) => (
                      <div
                        key={winner.id}
                        className="bg-background-50 border border-background-200 rounded-xl p-5 flex items-center gap-4"
                      >
                        <div className="w-20 h-20 rounded-full overflow-hidden bg-background-200 shrink-0">
                          {winner.photo ? (
                            <img
                              src={winner.photo}
                              alt={winner.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-foreground-400">
                              <i className="ri-user-star-line text-3xl" />
                            </div>
                          )}
                        </div>

                        <div>
                          <p className="font-heading font-bold text-foreground-950">
                            {winner.name}
                          </p>

                          {winner.prize && (
                            <p className="text-primary-600 text-sm font-label mt-1">
                              {winner.prize}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </>
        ) : (
          <section className="max-w-4xl mx-auto px-4 md:px-8 py-16 text-center">
            <i className="ri-gift-line text-6xl text-foreground-300 block mb-4" />

            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              Nenhum sorteio vigente no momento
            </h2>

            <p className="text-foreground-500 text-sm mt-3">
              Acompanhe esta página para participar dos próximos sorteios da M&amp;Z.
            </p>
          </section>
        )}

        {previousRaffles.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 md:px-8 py-14">
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                Sorteios Anteriores
              </h2>

              <p className="text-foreground-500 text-sm mt-2">
                Veja os sorteios já realizados e seus ganhadores.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {previousRaffles.map((raffle) => (
                <article
                  key={raffle.id}
                  className="bg-background-50 border border-background-200 rounded-xl overflow-hidden"
                >
                  {raffle.bannerImage && (
                    <div className="h-48 overflow-hidden bg-background-100">
                      {isVideoMedia(raffle.bannerImage) ? (
                        <video
                          src={raffle.bannerImage}
                          className="w-full h-full object-cover"
                          autoPlay
                          muted
                          loop
                          playsInline
                          preload="metadata"
                          aria-label={raffle.title}
                        />
                      ) : (
                        <img
                          src={raffle.bannerImage}
                          alt={raffle.title}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  )}

                  <div className="p-5">
                    <span className="text-primary-600 text-xs font-label font-bold uppercase">
                      Encerrado
                      {raffle.endDate
                        ? ` em ${formatDate(raffle.endDate)}`
                        : ""}
                    </span>

                    <h3 className="font-heading font-bold text-foreground-950 text-xl mt-2">
                      {raffle.title}
                    </h3>

                    {raffle.description && (
                      <p className="text-foreground-600 text-sm mt-2 line-clamp-3">
                        {raffle.description}
                      </p>
                    )}

                    {raffle.winners.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {raffle.winners.map((winner) => (
                          <div
                            key={winner.id}
                            className="flex items-center gap-2 text-sm"
                          >
                            <i className="ri-trophy-line text-primary-500" />
                            <span className="font-label font-semibold text-foreground-800">
                              {winner.name}
                            </span>
                            {winner.prize && (
                              <span className="text-foreground-500">
                                — {winner.prize}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() =>
                        setRegulationRaffle(raffle)
                      }
                      className="mt-5 text-primary-600 hover:text-primary-700 text-sm font-label font-semibold cursor-pointer"
                    >
                      <i className="ri-file-text-line mr-1" />
                      Ver regulamento
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}
      </main>

      {regulationRaffle && (
        <div
          className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4"
          onClick={() => setRegulationRaffle(null)}
        >
          <div
            className="w-full max-w-3xl max-h-[85vh] overflow-y-auto bg-background-50 rounded-xl border border-background-200"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sticky top-0 bg-background-50 border-b border-background-200 px-5 py-4 flex items-center justify-between gap-4">
              <div>
                <p className="text-xs text-primary-600 font-label font-bold uppercase tracking-wider">
                  Regulamento
                </p>

                <h3 className="font-heading font-bold text-foreground-950">
                  {regulationRaffle.title}
                </h3>
              </div>

              <button
                type="button"
                onClick={() => setRegulationRaffle(null)}
                className="w-9 h-9 rounded-full bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer"
                aria-label="Fechar regulamento"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="p-5 md:p-7 text-foreground-700 text-sm leading-relaxed whitespace-pre-wrap">
              {regulationRaffle.regulation ||
                "O regulamento deste sorteio ainda não foi publicado."}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}
